import {
  Typography,
  Box,
  keyframes,
  Tooltip,
  Link,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Game, GameSeries, StoneType } from "../../models";
import React from "react";
import {
  School,
  PersonOutline,
  CheckCircle,
  RadioButtonUnchecked,
  WarningAmber,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useTheme as useAppTheme } from "../../contexts/ThemeContext";
import { persistLocalStateToCloud } from "../../utils/playerState";
import { DialogControl } from "../../controls/DialogControl";

// Styled Components
const GameInfoCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(3),
  maxWidth: 800,
  width: "90%",
  backgroundColor: theme.palette.background.paper,
  border: `2px solid ${theme.palette.divider}`,
}));

const GameInfoContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  "&:last-child": {
    paddingBottom: theme.spacing(3),
  },
}));

const GameTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(0.5),
}));

const GameDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  fontStyle: "italic",
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 600,
  minWidth: 80,
}));

const PlayerBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "clickable",
})<{ clickable?: boolean }>(({ theme, clickable }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  flex: 1,
  cursor: clickable ? "pointer" : "default",
  transition: "all 0.2s",
  "&:hover": clickable
    ? {
        backgroundColor: theme.palette.action.selected,
        transform: "translateY(-2px)",
        boxShadow: theme.shadows[2],
      }
    : {},
}));

const PlayerName = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const PlayerNameInput = styled(TextField)(({ theme }) => ({
  flex: 1,
  "& .MuiInputBase-root": {
    backgroundColor: "transparent",
    fontSize: "0.875rem",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiInputBase-input": {
    padding: theme.spacing(0.5, 1),
    fontWeight: 500,
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: theme.spacing(1.5),
}));

interface GameInfoHeaderProps {
  game: Game;
  gameStarted: boolean;
  myTurn: boolean;
  player?: number;
  series?: GameSeries;
  stone?: StoneType;
  updateGame: (data: {
    title?: string;
    description?: string;
    expertMode?: boolean;
  }) => void;
  editMode?: boolean;
  selectPlayer?: (playerNum: string, game: Game, name?: string) => void;
}

export function GameInfoHeaderControl(props: GameInfoHeaderProps) {
  const { series, game, player, selectPlayer } = props;
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width="100%"
      position="relative"
      pt={{ xs: 2, sm: 4 }}
    >
      <GameInfoHeaderTitleBox
        player={player}
        game={game}
        series={series}
        selectPlayer={selectPlayer}
      />
    </Box>
  );
}

export function GameStatusInfoControl(props: {
  myTurn: boolean;
  stone: StoneType | undefined;
  player: number | undefined;
  game: Game;
}) {
  const { myTurn, stone, player, game } = props;
  const { t } = useTranslation();
  const blink = keyframes`
  from { opacity: 0.8; }
  to { opacity: 1; }
`;

  const getStatusMessage = () => {
    if (!myTurn && stone) {
      return t("gameControl:playerXTurn", {
        player:
          player === 2
            ? game.player1 || "Player 1"
            : game.player2 || "Player 2",
      });
    }
    if (myTurn && !stone) {
      return t("gameControl:playerXChoosesNextStone", {
        player:
          player === 2
            ? game.player1 || "Player 1"
            : game.player2 || "Player 2",
      });
    }
    if (myTurn && stone) {
      return t("gameControl:itsYourTurnLabel");
    }
    if (!myTurn && !stone) {
      return t("gameControl:chooseTheNextStone");
    }
  };

  return (
    <Card
      elevation={2}
      sx={{
        mb: 3,
        borderRadius: 3,
        maxWidth: 500,
        width: "90%",
        bgcolor: myTurn && stone ? "warning.light" : "grey.100",
        border: "2px solid",
        borderColor: myTurn && stone ? "warning.main" : "grey.300",
        animation:
          myTurn && stone
            ? `${blink} 1.5s ease-in-out infinite alternate`
            : "none",
      }}
    >
      <CardContent
        sx={{ p: 2, textAlign: "center", "&:last-child": { pb: 2 } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <Avatar
            sx={{
              bgcolor: myTurn && stone ? "warning.main" : "grey.400",
              width: 32,
              height: 32,
            }}
          >
            <PersonOutline fontSize="small" />
          </Avatar>
          <Typography variant="body1" fontWeight={500}>
            {getStatusMessage()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function GameInfoHeaderTitleBox(props: {
  game: Game;
  series?: GameSeries;
  player?: number;
  selectPlayer?: (playerNum: string, game: Game, name?: string) => void;
}) {
  const { t } = useTranslation();
  const { game, series, player, selectPlayer } = props;
  const { themeConfig } = useAppTheme();

  const [selectedPlayer, setSelectedPlayer] = React.useState<1 | 2 | null>(
    null
  );
  const [playerName, setPlayerName] = React.useState("");
  const [showWarning, setShowWarning] = React.useState(false);
  const [warningPlayer, setWarningPlayer] = React.useState<1 | 2>(1);

  const getSeriesModeName = (mode: number | null | undefined) => {
    if (!mode) return null;
    switch (mode) {
      case 1:
        return "Best of 1";
      case 3:
        return "Best of 3";
      case 5:
        return "Best of 5";
      case 7:
        return "Best of 7";
      default:
        return `Best of ${mode}`;
    }
  };

  const handlePlayerClick = (playerNum: 1 | 2) => {
    const isPlayerTaken = playerNum === 1 ? game.player1 : game.player2;

    if (isPlayerTaken) {
      // Show warning
      setWarningPlayer(playerNum);
      setShowWarning(true);
    } else {
      // Select player
      setSelectedPlayer(playerNum);
    }
  };

  const handleStartGame = async () => {
    if (selectedPlayer && selectPlayer) {
      await selectPlayer(String(selectedPlayer), game, playerName || undefined);

      // Save to localStorage
      localStorage.setItem(
        game.id,
        JSON.stringify({
          player: selectedPlayer,
          name: playerName || undefined,
        })
      );
      await persistLocalStateToCloud();

      // Reload to show updated state
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  };

  const handleConfirmTakenPlayer = () => {
    setShowWarning(false);
    // Set player in localStorage
    localStorage.setItem(game.id, JSON.stringify({ player: warningPlayer }));
    void persistLocalStateToCloud();

    // Reload to show as selected player
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  return (
    <GameInfoCard elevation={3}>
      <GameInfoContent>
        {/* Game Title */}
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <GameTitle variant="h5">{game.title}</GameTitle>
          {game.expertMode && <ExpertInfoIcon />}
        </Box>

        {/* Description */}
        {game.description && (
          <GameDescription variant="body2">{game.description}</GameDescription>
        )}

        <Divider sx={{ my: 2 }} />
        {/* Game Mode & Series Info */}
        <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          {/* Mode */}
          <Box display="flex" alignItems="center" gap={1}>
            <InfoLabel variant="body2">Mode:</InfoLabel>
            <Box display="flex" alignItems="center" gap={0.5}>
              {game.expertMode && (
                <School sx={{ color: "secondary.main", fontSize: 20 }} />
              )}
              <Typography
                variant="body2"
                fontWeight={600}
                color={game.expertMode ? "secondary.main" : "text.primary"}
              >
                {game.expertMode
                  ? t("gameControl:expertMode")
                  : t("gameControl:normalMode")}
              </Typography>
            </Box>
          </Box>

          {/* Series Info */}
          {series && (
            <>
              <Divider orientation="vertical" flexItem />
              <StatusChip
                label={getSeriesModeName(series.seriesMode)}
                size="small"
                color="primary"
                variant="outlined"
              />
              <StatusChip
                label={t("gameControl:gameXOfSeries", {
                  count: series?.gameIds?.length,
                })}
                size="small"
                color="info"
                variant="filled"
              />
            </>
          )}
        </Box>

        {/* Players */}
        <Box>
          <InfoLabel variant="body2" sx={{ mb: 2, display: "block" }}>
            {player
              ? t("gameControl:players")
              : t("gameControl:chooseYourPlayer")}
          </InfoLabel>
          <Box display="flex" gap={2} flexWrap="wrap">
            {/* Player 1 */}
            <PlayerBox
              clickable={!player && !game.player1}
              onClick={() => !player && handlePlayerClick(1)}
              sx={{
                border: selectedPlayer === 1 ? "2px solid" : "1px solid",
                borderColor: selectedPlayer === 1 ? "primary.main" : "divider",
              }}
            >
              <Avatar
                sx={{
                  bgcolor:
                    selectedPlayer === 1
                      ? "primary.main"
                      : player === 1
                      ? "primary.main"
                      : "action.disabled",
                  width: 32,
                  height: 32,
                }}
              >
                <PersonOutline sx={{ fontSize: 18 }} />
              </Avatar>
              <Box flex={1}>
                {selectedPlayer === 1 ? (
                  <PlayerNameInput
                    placeholder={t("ChoosePlayerControl.nameLabel")}
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    autoFocus
                    fullWidth
                  />
                ) : (
                  <PlayerName variant="body2">
                    {game.player1 || t("gameControl:player1")}
                  </PlayerName>
                )}
              </Box>
              {game.player1 && (
                <Chip
                  icon={<CheckCircle />}
                  label={t("ChoosePlayerControl.taken")}
                  size="small"
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {!game.player1 && !selectedPlayer && (
                <Chip
                  icon={<RadioButtonUnchecked />}
                  label={t("ChoosePlayerControl.available")}
                  size="small"
                  sx={{
                    bgcolor: "action.hover",
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                />
              )}
            </PlayerBox>

            {/* Player 2 */}
            <PlayerBox
              clickable={!player && !game.player2}
              onClick={() => !player && handlePlayerClick(2)}
              sx={{
                border: selectedPlayer === 2 ? "2px solid" : "1px solid",
                borderColor: selectedPlayer === 2 ? "primary.main" : "divider",
              }}
            >
              <Avatar
                sx={{
                  bgcolor:
                    selectedPlayer === 2
                      ? "primary.main"
                      : player === 2
                      ? "primary.main"
                      : "action.disabled",
                  width: 32,
                  height: 32,
                }}
              >
                <PersonOutline sx={{ fontSize: 18 }} />
              </Avatar>
              <Box flex={1}>
                {selectedPlayer === 2 ? (
                  <PlayerNameInput
                    placeholder={t("ChoosePlayerControl.nameLabel")}
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    autoFocus
                    fullWidth
                  />
                ) : (
                  <PlayerName variant="body2">
                    {game.player2 || t("gameControl:player2")}
                  </PlayerName>
                )}
              </Box>
              {game.player2 && (
                <Chip
                  icon={<CheckCircle />}
                  label={t("ChoosePlayerControl.taken")}
                  size="small"
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {!game.player2 && !selectedPlayer && (
                <Chip
                  icon={<RadioButtonUnchecked />}
                  label={t("ChoosePlayerControl.available")}
                  size="small"
                  sx={{
                    bgcolor: "action.hover",
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                />
              )}
            </PlayerBox>
          </Box>
        </Box>

        {/* Footer with Start Game Button - only show when no player selected yet */}
        {!player && (
          <>
            <Divider sx={{ mt: 3, mb: 2 }} />
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleStartGame}
                disabled={!selectedPlayer}
                size="large"
                sx={{
                  minWidth: 200,
                  fontWeight: 600,
                }}
              >
                {t("gameControl:startGame")}
              </Button>
            </Box>
          </>
        )}

        {/* Warning Dialog */}
        <DialogControl
          open={showWarning}
          onClose={() => setShowWarning(false)}
          dialogProps={{ maxWidth: "sm" }}
          paperSx={{
            borderRadius: 3,
            background: themeConfig.dialog.background,
            border: themeConfig.dialog.border,
          }}
          title={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                borderBottom: "2px solid",
                borderColor: "warning.main",
                bgcolor: themeConfig.dialog.headerBackground,
                pb: 1,
                pt: 1,
              }}
            >
              <WarningAmber sx={{ color: "warning.main", fontSize: 32 }} />
              <Typography variant="h6" fontWeight={600} color="warning.main">
                {t("ChoosePlayerControl.warningTitle")}
              </Typography>
            </Box>
          }
        >
          <Box
            sx={{
              p: 3,
              pt: 3,
              pb: 2,
            }}
          >
            <Typography
              variant="body1"
              color="text.primary"
              paragraph
              sx={{ mb: 2 }}
            >
              {t("ChoosePlayerControl.warningMessage", {
                player: warningPlayer === 1 ? game.player1 : game.player2,
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t("ChoosePlayerControl.warningSubtext")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                onClick={() => setShowWarning(false)}
                variant="outlined"
                sx={{
                  borderWidth: 2,
                  borderColor: "divider",
                  color: "text.secondary",
                  "&:hover": {
                    borderWidth: 2,
                    borderColor: "text.secondary",
                    bgcolor: "action.hover",
                  },
                }}
              >
                {t("ChoosePlayerControl.cancel")}
              </Button>
              <Button
                onClick={handleConfirmTakenPlayer}
                variant="contained"
                color="warning"
                startIcon={<CheckCircle />}
                sx={{
                  fontWeight: 600,
                  px: 3,
                }}
              >
                {t("ChoosePlayerControl.confirm")}
              </Button>
            </Box>
          </Box>
        </DialogControl>
      </GameInfoContent>
    </GameInfoCard>
  );
}
export function ExpertInfoIcon(props: { sx?: any }) {
  const { sx } = props;
  const { t } = useTranslation();
  return (
    <Link
      sx={{ textDecoration: "none", color: "black", ...sx }}
      href={"/info  "}
    >
      <Tooltip title={t("gameControl:expertModeTooltip")}>
        <School sx={{ ml: 1 }} />
      </Tooltip>
    </Link>
  );
}
